<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import KnowledgeAPI from 'dashboard/api/knowledge';
import SettingsLayout from '../settings/SettingsLayout.vue';
import BaseSettingsHeader from '../settings/components/BaseSettingsHeader.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';
import KnowledgeField from './components/KnowledgeField.vue';
import { ITEMS } from './config';

const props = defineProps({
  resource: { type: String, required: true },
});

const { t } = useI18n();
const config = computed(() => ITEMS[props.resource]);
const i18nPrefix = computed(() => `KNOWLEDGE.${config.value.i18nKey}`);

const items = ref([]);
const isLoading = ref(true);
const isSaving = ref(false);
const form = ref({});
const editingItem = ref(null);
const deletingItem = ref(null);
const formDialog = ref(null);
const deleteDialog = ref(null);

const tableHeaders = computed(() => [
  t(`${i18nPrefix.value}.TABLE_HEADER`),
  t('KNOWLEDGE.ACTIVE'),
  t('KNOWLEDGE.ACTIONS'),
]);
const isFormValid = computed(() =>
  config.value.fields
    .filter(field => field.required)
    .every(field => String(form.value[field.name] ?? '').trim())
);

const fetchItems = async () => {
  isLoading.value = true;
  try {
    const { data } = await KnowledgeAPI.getItems(props.resource);
    items.value = data.payload;
  } catch (error) {
    useAlert(t('KNOWLEDGE.LOAD_ERROR'));
  } finally {
    isLoading.value = false;
  }
};

const openForm = (item = null) => {
  editingItem.value = item;
  form.value = Object.fromEntries(
    config.value.fields.map(({ name }) => [name, item?.[name] ?? ''])
  );
  formDialog.value.open();
};

const saveItem = async () => {
  isSaving.value = true;
  const { key } = config.value;
  try {
    if (editingItem.value) {
      await KnowledgeAPI.updateItem(props.resource, key, editingItem.value.id, form.value);
    } else {
      await KnowledgeAPI.createItem(props.resource, key, form.value);
    }
    formDialog.value.close();
    useAlert(t('KNOWLEDGE.SAVE_SUCCESS'));
    await fetchItems();
  } catch (error) {
    useAlert(error?.response?.data?.message || t('KNOWLEDGE.SAVE_ERROR'));
  } finally {
    isSaving.value = false;
  }
};

const toggleActive = async item => {
  try {
    await KnowledgeAPI.updateItem(props.resource, config.value.key, item.id, { active: item.active });
  } catch (error) {
    item.active = !item.active;
    useAlert(t('KNOWLEDGE.SAVE_ERROR'));
  }
};

const confirmDelete = item => {
  deletingItem.value = item;
  deleteDialog.value.open();
};

const deleteItem = async () => {
  try {
    await KnowledgeAPI.deleteItem(props.resource, deletingItem.value.id);
    deleteDialog.value.close();
    useAlert(t('KNOWLEDGE.DELETE_SUCCESS'));
    await fetchItems();
  } catch (error) {
    useAlert(t('KNOWLEDGE.DELETE_ERROR'));
  }
};

onMounted(fetchItems);
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="t('KNOWLEDGE.LOADING')"
    :no-records-found="!items.length"
    :no-records-message="t(`${i18nPrefix}.EMPTY`)"
  >
    <template #header>
      <BaseSettingsHeader
        :title="t(`${i18nPrefix}.TITLE`)"
        :description="t(`${i18nPrefix}.DESCRIPTION`)"
      >
        <template #actions>
          <Button :label="t(`${i18nPrefix}.ADD`)" size="sm" @click="openForm()" />
        </template>
      </BaseSettingsHeader>
    </template>

    <template #body>
      <BaseTable :headers="tableHeaders" :items="items">
        <template #row="{ items: rows }">
          <BaseTableRow v-for="item in rows" :key="item.id" :item="item">
            <template #default>
              <BaseTableCell class="max-w-0">
                <div class="flex flex-col gap-1 min-w-0">
                  <span class="text-heading-3 text-n-slate-12 truncate block">
                    {{ item[config.titleField] }}
                  </span>
                  <p class="text-body-main text-n-slate-11 line-clamp-2">
                    {{ item[config.subtitleField] }}
                  </p>
                </div>
              </BaseTableCell>
              <BaseTableCell class="w-24">
                <Switch v-model="item.active" @change="toggleActive(item)" />
              </BaseTableCell>
              <BaseTableCell align="end" class="w-24">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="t('KNOWLEDGE.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    @click="openForm(item)"
                  />
                  <Button
                    v-tooltip.top="t('KNOWLEDGE.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    @click="confirmDelete(item)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <Dialog
      ref="formDialog"
      :title="t(editingItem ? `${i18nPrefix}.EDIT_TITLE` : `${i18nPrefix}.ADD`)"
      :confirm-button-label="t('KNOWLEDGE.SAVE')"
      :disable-confirm-button="!isFormValid"
      :is-loading="isSaving"
      width="2xl"
      overflow-y-auto
      @confirm="saveItem"
    >
      <div class="grid grid-cols-1 gap-4 md:grid-cols-2">
        <KnowledgeField
          v-for="field in config.fields"
          :key="field.name"
          v-model="form[field.name]"
          :field="field"
          :i18n-prefix="i18nPrefix"
          :class="{ 'md:col-span-2': field.kind !== 'input' }"
        />
      </div>
    </Dialog>

    <Dialog
      ref="deleteDialog"
      type="alert"
      :title="t('KNOWLEDGE.DELETE_CONFIRM_TITLE')"
      :description="deletingItem?.[config.titleField]"
      :confirm-button-label="t('KNOWLEDGE.DELETE')"
      @confirm="deleteItem"
    />
  </SettingsLayout>
</template>
